import React from 'react';

/**
 * Data source types for the terminal
 * - live: Real-time API data (green)
 * - official: Official government/institutional sources (blue)
 * - verified: Verified static data from authoritative sources (yellow)
 * - calculated: NATEX-calculated or derived metrics (gray)
 */
export type DataSourceType = 'live' | 'official' | 'verified' | 'calculated';

export interface DataSourceConfig {
  type: DataSourceType;
  label: string;
  tooltip?: string;
  lastUpdated?: Date | string | null;
}

interface DataSourceBadgeProps {
  source: DataSourceType | DataSourceConfig;
  showLabel?: boolean;
  showTooltip?: boolean;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}

const SOURCE_STYLES: Record<DataSourceType, { color: string; bg: string; border: string; glow: string; label: string }> = {
  live: {
    color: 'text-green-400',
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
    glow: 'shadow-green-500/20',
    label: 'Live',
  },
  official: {
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    glow: 'shadow-blue-500/20',
    label: 'Official',
  },
  verified: {
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    glow: 'shadow-amber-500/20',
    label: 'Verified',
  },
  calculated: {
    color: 'text-gray-400',
    bg: 'bg-gray-500/10',
    border: 'border-gray-500/30',
    glow: 'shadow-gray-500/20',
    label: 'NATEX',
  },
};

const SIZE_STYLES = {
  xs: {
    dot: 'w-1.5 h-1.5',
    text: 'text-[9px]',
    padding: 'px-1 py-0.5',
    gap: 'gap-0.5',
  },
  sm: {
    dot: 'w-2 h-2',
    text: 'text-[10px]',
    padding: 'px-1.5 py-0.5',
    gap: 'gap-1',
  },
  md: {
    dot: 'w-2.5 h-2.5',
    text: 'text-xs',
    padding: 'px-2 py-1',
    gap: 'gap-1.5',
  },
};

/**
 * DataSourceBadge - Visual indicator for data source type
 *
 * Color coding:
 * - Green (live): Real-time API data (CBU rates, etc.)
 * - Blue (official): Official institutional sources (IMF, World Bank, etc.)
 * - Yellow (verified): Verified static data from authoritative reports
 * - Gray (calculated): NATEX-calculated or proprietary metrics
 */
export const DataSourceBadge: React.FC<DataSourceBadgeProps> = ({
  source,
  showLabel = false,
  showTooltip = true,
  size = 'sm',
  className = '',
}) => {
  const sourceType = typeof source === 'string' ? source : source.type;
  const customLabel = typeof source === 'object' ? source.label : undefined;
  const tooltip = typeof source === 'object' ? source.tooltip : undefined;
  const lastUpdated = typeof source === 'object' ? source.lastUpdated : undefined;

  const styles = SOURCE_STYLES[sourceType];
  const sizeStyles = SIZE_STYLES[size];

  const formatDate = (date: Date | string | null | undefined): string => {
    if (!date) return '';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const tooltipText = tooltip || `${styles.label} data${lastUpdated ? ` (Updated: ${formatDate(lastUpdated)})` : ''}`;

  // Dot only (no label)
  if (!showLabel) {
    return (
      <span
        className={`inline-flex items-center ${className}`}
        title={showTooltip ? tooltipText : undefined}
      >
        <span
          className={`${sizeStyles.dot} rounded-full ${styles.bg} border ${styles.border} ${
            sourceType === 'live' ? 'animate-pulse' : ''
          }`}
          style={{
            boxShadow: sourceType === 'live' ? `0 0 4px currentColor` : undefined,
          }}
        />
      </span>
    );
  }

  // Badge with label
  return (
    <span
      className={`inline-flex items-center ${sizeStyles.padding} ${sizeStyles.gap} rounded ${styles.bg} border ${styles.border} ${className}`}
      title={showTooltip ? tooltipText : undefined}
    >
      <span
        className={`${sizeStyles.dot} rounded-full ${styles.bg} border ${styles.border} ${
          sourceType === 'live' ? 'animate-pulse' : ''
        }`}
      />
      <span className={`${sizeStyles.text} ${styles.color} font-medium uppercase tracking-wider`}>
        {customLabel || styles.label}
      </span>
    </span>
  );
};

/**
 * DataSourceLegend - Shows legend of all source types
 */
export const DataSourceLegend: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`flex flex-wrap gap-3 ${className}`}>
      {(Object.keys(SOURCE_STYLES) as DataSourceType[]).map(type => (
        <DataSourceBadge key={type} source={type} showLabel size="sm" />
      ))}
    </div>
  );
};

/**
 * DataSourceIndicator - Inline indicator with source name
 */
interface DataSourceIndicatorProps {
  source: string;
  type: DataSourceType;
  lastUpdated?: Date | string | null;
  className?: string;
}

export const DataSourceIndicator: React.FC<DataSourceIndicatorProps> = ({
  source,
  type,
  lastUpdated,
  className = '',
}) => {
  const styles = SOURCE_STYLES[type];

  return (
    <div className={`flex items-center gap-2 text-xs ${className}`}>
      <DataSourceBadge source={type} size="xs" />
      <span className={`${styles.color} font-medium`}>{source}</span>
      {lastUpdated && (
        <span className="text-gray-500">
          {typeof lastUpdated === 'string'
            ? lastUpdated
            : lastUpdated.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
        </span>
      )}
    </div>
  );
};

/**
 * Common data source configurations
 */
export const DATA_SOURCES = {
  // Live APIs
  CBU: { type: 'live' as const, label: 'CBU', tooltip: 'Central Bank of Uzbekistan - Live exchange rates' },
  FX_API: { type: 'live' as const, label: 'FX', tooltip: 'Live FX rates API' },

  // Official Sources
  WORLD_BANK: { type: 'official' as const, label: 'World Bank', tooltip: 'World Bank Open Data API' },
  IMF: { type: 'official' as const, label: 'IMF', tooltip: 'IMF World Economic Outlook' },
  ADB: { type: 'official' as const, label: 'ADB', tooltip: 'Asian Development Bank' },

  // Verified Static
  UNCTAD: { type: 'verified' as const, label: 'UNCTAD', tooltip: 'UNCTAD World Investment Report' },
  STAT_UZ: { type: 'verified' as const, label: 'Stat.uz', tooltip: 'Statistics Agency of Uzbekistan' },
  SP: { type: 'verified' as const, label: 'S&P', tooltip: "Standard & Poor's Rating" },
  FITCH: { type: 'verified' as const, label: 'Fitch', tooltip: 'Fitch Ratings' },
  MOODYS: { type: 'verified' as const, label: "Moody's", tooltip: "Moody's Rating" },

  // Calculated/Proprietary
  NATEX: { type: 'calculated' as const, label: 'NATEX', tooltip: 'NATEX proprietary calculation' },
  DERIVED: { type: 'calculated' as const, label: 'Derived', tooltip: 'Calculated from source data' },
};

export default DataSourceBadge;
